import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Square } from "lucide-react";
import { VOICE_FALLBACK_MSG, voiceErrorMessage } from "@/components/VoiceTextarea";

/**
 * VoiceInput — single-line or multi-line text field with a compact mic button.
 * Pure browser Web Speech API. No audio is uploaded — only the transcript.
 * Hides the mic and shows a typed-fallback message when speech is unavailable.
 * Dictation appends to the CURRENT text (never erases typed text).
 */
export default function VoiceInput({
  value,
  onChange,
  placeholder,
  testId,
  micTestId,
  disabled,
  type = "text",
  multiline = false,
  rows = 4,
  maxChars,
  className = "",
  inputProps = {},
}) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(true);
  const [interim, setInterim] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const recogRef = useRef(null);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  valueRef.current = value;
  onChangeRef.current = onChange;

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setSupported(false); return; }
    const r = new SR();
    r.continuous = multiline;
    r.interimResults = true;
    r.lang = "en-US";
    r.onresult = (ev) => {
      let finalText = "";
      let interimText = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const res = ev.results[i];
        if (res.isFinal) finalText += res[0].transcript;
        else interimText += res[0].transcript;
      }
      if (finalText) {
        const cleaned = finalText.replace(/\s+/g, " ").trim();
        const prev = valueRef.current || "";
        const sep = prev && !prev.endsWith(" ") ? " " : "";
        const next = prev + sep + cleaned;
        onChangeRef.current(maxChars ? next.slice(0, maxChars) : next);
      }
      setInterim(interimText);
    };
    r.onend = () => { setListening(false); setInterim(""); };
    r.onerror = (ev) => {
      setListening(false);
      setInterim("");
      setErrMsg(voiceErrorMessage(ev?.error));
    };
    recogRef.current = r;
    return () => { try { r.stop(); } catch (e) { /* noop */ } };
  }, [multiline, maxChars]);

  const start = () => {
    if (!recogRef.current || disabled) return;
    setErrMsg("");
    try { recogRef.current.start(); setListening(true); } catch (e) { /* already running */ }
  };
  const stop = () => {
    if (!recogRef.current) return;
    try { recogRef.current.stop(); } catch (e) { /* noop */ }
    setListening(false);
  };

  const display = (value || "") + (interim ? (value && !value.endsWith(" ") ? " " : "") + interim : "");
  const handleChange = (e) => {
    setErrMsg("");
    const v = e.target.value;
    onChange(maxChars ? v.slice(0, maxChars) : v);
  };

  const MicBtn = supported ? (
    <button
      type="button"
      data-testid={micTestId}
      onClick={listening ? stop : start}
      disabled={disabled}
      title={listening ? "Stop voice input" : "Speak your answer"}
      className="inline-flex items-center justify-center rounded-full transition disabled:opacity-30"
      style={{
        background: listening ? "#B91C1C" : "#0B2D59",
        color: "#F4F8FB",
        width: multiline ? "auto" : "2.25rem",
        height: "2.25rem",
        padding: multiline ? "0 0.85rem" : 0,
        flex: "none",
      }}
      aria-label={listening ? "Stop recording" : "Start voice input"}
    >
      {listening ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
      {multiline && <span className="ml-2 text-sm font-semibold">{listening ? "Stop" : "Speak"}</span>}
    </button>
  ) : null;

  const StatusLine = (!supported || errMsg) ? (
    <div className="text-xs inline-flex items-center gap-1" style={{ color: supported ? "#8A6D1D" : "#475569" }}>
      <MicOff className="w-3 h-3 shrink-0" /> {supported ? errMsg : VOICE_FALLBACK_MSG}
    </div>
  ) : null;

  if (multiline) {
    return (
      <div className="space-y-2">
        <div className="relative">
          <textarea
            data-testid={testId}
            rows={rows}
            className={"p2p-input font-sans " + className}
            style={{ minHeight: rows * 24 + 24 }}
            placeholder={placeholder}
            value={display}
            onChange={handleChange}
            maxLength={maxChars}
            disabled={disabled}
            {...inputProps}
          />
          {supported && (
            <div className="absolute right-3 bottom-3">{MicBtn}</div>
          )}
        </div>
        {(StatusLine || maxChars || listening) && (
          <div className="flex items-center justify-between text-xs" style={{ color: "#475569" }}>
            <span>{StatusLine || (listening ? "Listening… you can pause anytime." : "")}</span>
            {maxChars && <span>{(value || "").length}/{maxChars}</span>}
          </div>
        )}
      </div>
    );
  }

  // single-line
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <input
          type={type}
          data-testid={testId}
          className={"p2p-input " + className}
          placeholder={placeholder}
          value={display}
          onChange={handleChange}
          disabled={disabled}
          {...inputProps}
        />
        {MicBtn}
      </div>
      {errMsg && StatusLine}
    </div>
  );
}
